/**
 * @file: The rules of the play report, read from the play log.
 *
 * The play log and the play queue are one table. A row is queued until a
 * player reports a start. A start stamp alone is not proof that the clip was
 * on screen. The queue also stamps a start on every unplayed row when it drops
 * them at a playlist change. The result field tells the two apart. This module
 * holds that reading in one place, so every consumer counts the same rows.
 *
 * The module is in shared/ because the server aggregates the log and the
 * client shows the aggregate. Both must name the states, the keys and the
 * thresholds the same way. Otherwise the numbers on the page do not match the
 * rows they came from.
 */

/**
 * The five states of a log row. Only the first three were on a screen.
 * A skipped row has a start stamp but no player behind it.
 */
const PLAY_STATES = Object.freeze({
  ENDED: 'ended',
  FAILED: 'failed',
  UNENDED: 'unended',
  SKIPPED: 'skipped',
  QUEUED: 'queued'
})

/**
 * The two result strings the player writes. The player component imports
 * them, so the strings have one home. Every other result on an ended row is
 * an error message. Every other result on a started row is the reason the
 * queue dropped it.
 */
const PLAYER_RESULTS = Object.freeze({
  STARTED: 'Playback started',
  ENDED: 'Playback ended'
})

/**
 * A pause longer than this, after the end of a play, is a period with no
 * contact. One minute covers a slow network and the ten-second poll that
 * fills the queue, without hiding a real pause.
 */
const NO_CONTACT_SLACK_MS = 60000

/**
 * The longest window the report reads. A festival week is about 65,000 rows.
 * The server reads a window in one query and adds it up in memory, so a much
 * longer window needs a different plan. The client reads the same number, so
 * it can stop a window before the request is sent.
 */
const MAX_WINDOW_DAYS = 31

const MS_PER_DAY = 24 * 60 * 60 * 1000
const HOUR_MS = 60 * 60 * 1000
const MINUTE_MS = 60 * 1000

/** The two reasons a window cannot be read. The server and the client map them to their own text. */
const WINDOW_PROBLEMS = Object.freeze({
  ORDER: 'order',
  LENGTH: 'length'
})

/** The sentence the operator reads for each reason. */
const WINDOW_PROBLEM_TEXT = Object.freeze({
  [WINDOW_PROBLEMS.ORDER]: 'The To time must be after the From time',
  [WINDOW_PROBLEMS.LENGTH]: `The window can be ${MAX_WINDOW_DAYS} days at most`
})

/**
 * The reason a window cannot be read, or null when the window is fine. Both
 * sides test the same two rules: the end comes after the start, and the
 * window is not longer than the limit.
 */
function windowProblem (fromMs, toMs) {
  if (!(toMs > fromMs)) {
    return WINDOW_PROBLEMS.ORDER
  }
  if (toMs - fromMs > MAX_WINDOW_DAYS * MS_PER_DAY) {
    return WINDOW_PROBLEMS.LENGTH
  }
  return null
}

/** A DATETIME comes as a Date from the driver, as a string from the wire, and as a number from a test. */
const asMs = value => (value instanceof Date ? value.getTime() : typeof value === 'number' ? value : new Date(value).getTime())

/**
 * The key of one media in the log. The log has no media id, so the type and
 * the display name are the identity. JSON keeps two names apart whatever
 * characters they hold.
 */
function mediaKey ({ type, name }) {
  return JSON.stringify([type, name])
}

/**
 * Read the state of one log row from its three stamps.
 *
 * An end stamp means the player stopped the clip, with or without an error.
 * A player can fail before it reports a start, so an end stamp counts with or
 * without a start. A start stamp alone is a play only when the player wrote
 * it. The queue writes its reason instead.
 */
function playState ({ timePlayed, playbackEnded, result }) {
  if (playbackEnded) {
    return result === PLAYER_RESULTS.ENDED ? PLAY_STATES.ENDED : PLAY_STATES.FAILED
  }
  if (!timePlayed) {
    return PLAY_STATES.QUEUED
  }
  return result === PLAYER_RESULTS.STARTED ? PLAY_STATES.UNENDED : PLAY_STATES.SKIPPED
}

/** @type {Set<string>} */
const REPORTED = new Set([PLAY_STATES.ENDED, PLAY_STATES.FAILED, PLAY_STATES.UNENDED])

/**
 * True when a player said something about the row. A report is contact, so
 * the gap rule reads these rows. A queued row and a dropped row are not.
 */
function reported (row) {
  return REPORTED.has(playState(row))
}

/**
 * True when the clip reached a screen. An ended or an unended play did. A
 * failed play did only when it had started. A failure before the start is a
 * report and a failure, but not a play a sponsor paid for.
 */
function reachedScreen (row) {
  const state = playState(row)
  if (state === PLAY_STATES.FAILED) {
    return Boolean(row.timePlayed)
  }
  return state === PLAY_STATES.ENDED || state === PLAY_STATES.UNENDED
}

/** The stamp a play is placed at. A play that failed before its start has only its end stamp. */
function startOf (row) {
  return row.timePlayed || row.playbackEnded
}

const startMs = row => asMs(startOf(row))

/**
 * The time the clip was on screen, in milliseconds. Only an ended play has a
 * measured time. A failed play stopped early, and its time is not screen time
 * for a sponsor, so it counts zero.
 */
function screenMs (row) {
  if (playState(row) !== PLAY_STATES.ENDED || !row.timePlayed) {
    return 0
  }
  return Math.max(0, asMs(row.playbackEnded) - asMs(row.timePlayed))
}

/**
 * The instant a play ended. The player's end stamp is the measure when there
 * is one. A play with no end stamp is expected to end after its planned
 * duration.
 */
function endMs (row) {
  if (row.playbackEnded) {
    return asMs(row.playbackEnded)
  }
  return startMs(row) + (row.duration || 0)
}

/**
 * True when an unended play is still on screen. The player reports the end
 * when the clip stops, so a play that began less than its planned duration
 * plus the slack ago has had no time to end. Such a play is not a finding.
 *
 * The comparison reads `updatedAt`, the stamp the server wrote when the row
 * last changed, when the row has it. The player writes `timePlayed` from its
 * own clock, and `nowMs` is the server's clock. Two clocks must not meet in
 * one subtraction.
 */
function isPlayingNow (row, nowMs) {
  if (playState(row) !== PLAY_STATES.UNENDED) {
    return false
  }
  const began = asMs(row.updatedAt || startOf(row))
  return nowMs - began <= (row.duration || 0) + NO_CONTACT_SLACK_MS
}

/**
 * The periods in which a scene sent no report, between two of its plays.
 *
 * `plays` are the reported rows of ONE scene, in any order. When the next
 * play starts more than the slack after the end of the play before it, the
 * pause is a period with no contact. The player may have played from its
 * cache in that period, so the word is contact, not outage.
 *
 * A pause before the first play or after the last play of the window is not a
 * gap. The log cannot tell a quiet screen from a screen with nothing
 * scheduled. The heartbeat of the scene tells whether a screen is quiet now.
 *
 * Returns `{ from, to, ms, lastName }` per period, in time order, with `from`
 * and `to` as milliseconds since the epoch.
 */
function noContactGaps (plays) {
  const sorted = plays
    .map(play => ({ start: startMs(play), end: endMs(play), name: play.itemName }))
    .sort((a, b) => a.start - b.start)

  return sorted.slice(1).flatMap((play, i) => {
    const previous = sorted[i]
    const pause = play.start - previous.end
    return pause > NO_CONTACT_SLACK_MS
      ? [{ from: previous.end, to: play.start, ms: pause, lastName: previous.name }]
      : []
  })
}

/** True when the browser or Node knows the zone by that name. */
function isTimeZone (name) {
  try {
    return typeof name === 'string' && Boolean(new Intl.DateTimeFormat('en', { timeZone: name }))
  } catch (error) {
    return false
  }
}

const CLOCK_OPTIONS = Object.freeze({
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23'
})

/**
 * The formatters of a zone, made on first use. To make a formatter costs
 * about a hundred times more than to use it, and the report reads a clock
 * once per row of a window. A formatter has no state, so the cache changes no
 * result.
 */
const formatters = new Map()

function formattersFor (timeZone) {
  if (!formatters.has(timeZone)) {
    formatters.set(timeZone, {
      clock: new Intl.DateTimeFormat('en-GB', { ...CLOCK_OPTIONS, timeZone }),
      weekday: new Intl.DateTimeFormat('en-GB', { weekday: 'short', timeZone })
    })
  }
  return formatters.get(timeZone)
}

/**
 * The clocks already read, by zone and minute. A window holds thousands of
 * rows but at most 44,640 minutes, so most reads repeat an earlier one. The
 * cache is emptied when it grows past its limit, so it cannot grow without
 * end.
 */
const clocks = new Map()
const MAX_CACHED_CLOCKS = 100000

function readClock (ms, timeZone) {
  const parts = formattersFor(timeZone).clock.formatToParts(new Date(ms))
  const p = parts.reduce((byType, part) => Object.assign(byType, { [part.type]: part.value }), {})
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`
}

/**
 * The wall clock of an instant in a zone, as 'YYYY-MM-DDTHH:mm'.
 *
 * The zone is an IANA name such as 'Europe/Copenhagen'. It is a zone and not
 * an offset. A window of thirty days can cross the change to or from summer
 * time, and one offset would cut half of those days one hour off. The runtime
 * knows the rules of every zone, so no library is needed.
 */
function zoneClock (value, timeZone) {
  const ms = asMs(value)
  const key = `${timeZone}|${Math.floor(ms / MINUTE_MS)}`
  if (!clocks.has(key)) {
    if (clocks.size >= MAX_CACHED_CLOCKS) {
      clocks.clear()
    }
    clocks.set(key, readClock(ms, timeZone))
  }
  return clocks.get(key)
}

/** The short weekday of an instant in a zone, such as 'Fri'. */
function zoneWeekday (value, timeZone) {
  return formattersFor(timeZone).weekday.format(new Date(asMs(value)))
}

/** The festival day of an instant, as 'YYYY-MM-DD'. This is the day key of the report. */
function festivalDay (value, timeZone) {
  return zoneClock(value, timeZone).slice(0, 10)
}

/** The festival hour of an instant, as 'YYYY-MM-DDTHH:00'. This is the hour key of the report. */
function festivalHour (value, timeZone) {
  return `${zoneClock(value, timeZone).slice(0, 13)}:00`
}

/**
 * The readers of the three keys. They stand beside the writers, so a change
 * of the key format changes both in one file.
 */
const dayOfKey = key => key.slice(0, 10)
const hourLabel = key => key.slice(11, 13)
const clockOfKey = key => key.slice(11, 16)
const dayLabel = key => `${key.slice(8, 10)}/${key.slice(5, 7)}`

/**
 * Every festival hour of a window, in order, as the server writes them.
 *
 * The walk steps one hour at a time from the start of the window, and the
 * last step is the instant before the end. Every step maps to a festival hour,
 * so a window from 08:30 to 20:15 gets the hours 08 to 20. The change from
 * summer time repeats one wall-clock hour, and the set drops the repeat.
 */
function hourKeysBetween (from, to, timeZone) {
  const start = asMs(from)
  const last = asMs(to) - 1
  if (!(last >= start)) {
    return []
  }
  const steps = Array.from({ length: Math.floor((last - start) / HOUR_MS) + 1 }, (unused, i) => start + i * HOUR_MS)
  return [...new Set([...steps, last].map(ms => festivalHour(ms, timeZone)))]
}

export {
  PLAY_STATES,
  PLAYER_RESULTS,
  NO_CONTACT_SLACK_MS,
  MAX_WINDOW_DAYS,
  WINDOW_PROBLEMS,
  WINDOW_PROBLEM_TEXT,
  windowProblem,
  asMs,
  mediaKey,
  playState,
  reported,
  reachedScreen,
  startOf,
  screenMs,
  isPlayingNow,
  noContactGaps,
  isTimeZone,
  zoneClock,
  zoneWeekday,
  festivalDay,
  festivalHour,
  dayOfKey,
  hourLabel,
  clockOfKey,
  dayLabel,
  hourKeysBetween
}
